import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import {
  PlusIcon,
  EyeIcon,
  BuildingOfficeIcon,
  GiftIcon,
  CurrencyDollarIcon,
  CubeIcon,
  SparklesIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PencilSquareIcon,
  TrashIcon,
  EyeSlashIcon,
  UserGroupIcon,
  CalendarIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';

// Add CSS for line-clamp if not available
const styles = `
  .line-clamp-1 {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
  }
  .line-clamp-2 {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

// Normalize image URLs to work in dev (3000->5000) and prod
const resolveImageUrl = (img?: string) => {
  if (!img) return '';
  const normalized = String(img).replace(/\\/g, '/');
  if (/^(https?:)?\/\//i.test(normalized) || normalized.startsWith('data:')) {
    return normalized;
  }
  const idx = normalized.indexOf('/uploads/');
  const relPath = idx !== -1 ? normalized.slice(idx) : (normalized.startsWith('/') ? normalized : `/${normalized}`);
  const isDev = typeof window !== 'undefined' && window.location.port === '3000';
  const base = isDev ? 'http://localhost:5000' : '';
  return `${base}${relPath}`;
};

interface Package {
  _id: string;
  name: string;
  description: string;
  bannerImage: string;
  priceUSD: number;
  priceAUD: number;
  resorts: Array<{
    _id: string;
    name: string;
    destination: string;
    image: string;
  }>;
  features: string[];
  inclusions: string[];
  exclusions: string[];
  premiumFeatures: string[];
  termsAndConditions: string[];
  gallery: string[];
  highlights: string[];
  validityPeriod: number;
  isActive: boolean;
  isLive?: boolean;
  maxGuests: number;
  packageCode: string;
  purchaseCount?: number;
  createdAt: string;
  slug?: string;
  sortOrder?: number;
}

const AdminPackages: React.FC = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  
  // Legacy modal form states removed in favor of dedicated pages

  // Resort selection happens in dedicated create/edit pages

  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch packages from API
  const fetchPackages = async () => {
    try {
      setLoading(true);
      const headers: HeadersInit = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      // Use public endpoint to avoid auth issues for now
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/packages`, {
        withCredentials: true
      });
      const packagesData = response.data.packages || response.data;
      setPackages(Array.isArray(packagesData) ? packagesData : []);
    } catch (error) {
      console.error('Error fetching packages:', error);
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLive = async (id: string) => {
    try {
      const response = await axios.patch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/packages/${id}/toggle-live`);
      fetchPackages();
    } catch (error) {
      console.error('Error toggling live status:', error);
      alert('Failed to toggle live status');
    }
  };

  useEffect(() => {
    fetchPackages();
  }, [token]);

  // Legacy modal save removed; dedicated pages handle create/edit

  // Resort fetch for selection moved to dedicated pages

  const filteredPackages = packages.filter(pkg => {
    const statusMatch = filterStatus === 'all' || 
      (filterStatus === 'active' && pkg.isActive) ||
      (filterStatus === 'inactive' && !pkg.isActive);
    return statusMatch;
  });

  const handleAdd = () => {
    navigate('/admin/packages/create');
  };

  // Edit navigates to dedicated page via button action

  // Modal gallery handlers removed

  // Modal features handlers removed

  // Modal inclusions handlers removed

  // Modal exclusions handlers removed

  // Modal premium features handlers removed

  // Modal highlights handlers removed

  const handleToggleStatus = async (id: string) => {
    try {
      const response = await axios.patch(`/api/packages/${id}/toggle-status`);
      fetchPackages();
    } catch (error) {
      console.error('Error toggling package status:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this package?')) {
      try {
        const response = await axios.delete(`/api/packages/${id}`);
        fetchPackages();
      } catch (error) {
        console.error('Error deleting package:', error);
        alert('Failed to delete package');
      }
    }
  };

  const handleClone = async (packageData: Package) => {
    try {
      // Create a cloned package with modified name and reset some fields
      const clonedPackage = {
        ...packageData,
        name: `${packageData.name} (Copy)`,
        packageCode: '', // Will be auto-generated
        isActive: false, // Start as inactive
        isLive: false, // Start as draft
        purchaseCount: 0, // Reset purchase count
        _id: undefined, // Remove ID so it creates new
        createdAt: undefined, // Will be set by backend
        slug: undefined // Will be auto-generated
      };
      
      const response = await axios.post(`/api/packages`, clonedPackage);
      
      if (response.data && response.data._id) {
        // Navigate to edit the cloned package
        navigate(`/admin/packages/edit/${response.data._id}`);
      } else {
        fetchPackages(); // Refresh the list
        alert('Package cloned successfully!');
      }
    } catch (error) {
      console.error('Error cloning package:', error);
      alert('Failed to clone package');
    }
  };

  return (
    <>
      <Helmet>
        <title>Packages - Bali Getaway Admin</title>
      </Helmet>

      {/* Modern Header Section */}
      <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Navigation Header */}
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg hover:bg-white hover:shadow-md transition-all duration-200"
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              Back to Dashboard
            </button>

            <div className="hidden md:flex items-center space-x-2 bg-white/60 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-gray-700">Packages Management</span>
            </div>
          </div>

          {/* Page Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl shadow-lg mb-6">
              <GiftIcon className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent mb-3">
              Package Management
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Create, manage, and optimize your travel packages to provide amazing experiences for your customers
            </p>
          </div>

          {/* Modern Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Total Packages */}
            <div className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300">
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
                      <CubeIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-gray-900 mb-1">{packages.length}</p>
                      <p className="text-sm font-medium text-gray-600">Total Packages</p>
                    </div>
                  </div>
                  <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                </div>
              </div>
            </div>

            {/* Active Packages */}
            <div className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300">
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-green-500 rounded-xl flex items-center justify-center">
                      <CheckCircleIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-gray-900 mb-1">
                        {packages.filter(p => p.isActive).length}
                      </p>
                      <p className="text-sm font-medium text-gray-600">Active Packages</p>
                    </div>
                  </div>
                  <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                </div>
              </div>
            </div>

            {/* Total Sales */}
            <div className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300">
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                      <ChartBarIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-gray-900 mb-1">
                        {packages.reduce((sum, p) => sum + (p.purchaseCount || 0), 0)}
                      </p>
                      <p className="text-sm font-medium text-gray-600">Total Sales</p>
                    </div>
                  </div>
                  <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                </div>
              </div>
            </div>

            {/* Resort Associations */}
            <div className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300">
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
                      <BuildingOfficeIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-gray-900 mb-1">
                        {packages.reduce((sum, p) => sum + p.resorts.length, 0)}
                      </p>
                      <p className="text-sm font-medium text-gray-600">Resort Links</p>
                    </div>
                  </div>
                  <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Modern Filter & Actions */}
          <div className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden mb-8">
            <div className="p-6">
              <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
                {/* Filter Section */}
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl flex items-center justify-center">
                    <FunnelIcon className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      Filter Packages
                    </label>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value as 'all' | 'active' | 'inactive')}
                      className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200 bg-white min-w-[160px]"
                    >
                      <option value="all">All Statuses</option>
                      <option value="active">Active Only</option>
                      <option value="inactive">Inactive Only</option>
                    </select>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="flex items-center space-x-3">
                  <Link
                    to="/packages"
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-indigo-600 shadow-lg hover:shadow-xl transition-all duration-200"
                    title="View public packages page"
                  >
                    <EyeIcon className="h-5 w-5 mr-2" />
                    View Public Site
                  </Link>
                  <button
                    onClick={handleAdd}
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-xl hover:from-amber-600 hover:to-orange-600 shadow-lg hover:shadow-xl transition-all duration-200"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Create Package
                  </button>
                </div>
              </div>

              {/* Filter Summary */}
              <div className="mt-6 pt-6 border-t border-gray-100">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-4">
                    <span className="text-gray-600">
                      Showing {filteredPackages.length} of {packages.length} packages
                    </span>
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                        {packages.filter(p => p.isActive).length} Active
                      </span>
                      <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded-full">
                        {packages.filter(p => !p.isActive).length} Inactive
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => fetchPackages()}
                    className="inline-flex items-center px-3 py-1 text-gray-600 hover:text-gray-800 transition-colors"
                    title="Refresh packages"
                  >
                    <ArrowPathIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Modern Packages Grid */}
          <div className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <GiftIcon className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">
                      All Packages ({filteredPackages.length})
                    </h2>
                    <p className="text-amber-100 text-sm">
                      Manage your travel packages and offerings
                    </p>
                  </div>
                </div>
                <div className="hidden md:flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-white text-sm font-medium">
                      {packages.filter(p => p.isActive).length} Active
                    </span>
                  </div>
                  <div className="w-px h-6 bg-white/30"></div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                    <span className="text-white text-sm font-medium">
                      {packages.filter(p => !p.isActive).length} Inactive
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {filteredPackages.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-32 h-32 bg-gradient-to-r from-amber-100 to-orange-100 rounded-3xl flex items-center justify-center mx-auto mb-8">
                    <GiftIcon className="h-16 w-16 text-amber-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {packages.length === 0 ? 'No packages yet' : 'No packages match your filter'}
                  </h3>
                  <p className="text-gray-600 mb-8 max-w-lg mx-auto text-lg">
                    {packages.length === 0
                      ? 'Ready to create your first amazing package? Let\'s build something incredible together!'
                      : 'Try adjusting your filters or create a new package to get started.'}
                  </p>
                  {packages.length === 0 && (
                    <button
                      onClick={() => navigate('/admin/packages/create')}
                      className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-2xl hover:from-amber-600 hover:to-orange-600 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                    >
                      <PlusIcon className="h-6 w-6 mr-3" />
                      Create Your First Package
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {filteredPackages.map((pkg) => (
                    <div key={pkg._id} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group">
                      {/* Package Image & Basic Info */}
                      <div className="relative">
                        <div className="aspect-[16/9] overflow-hidden">
                          <img
                            src={resolveImageUrl(pkg.bannerImage || (pkg.gallery && pkg.gallery[0])) || '/api/placeholder/400/225'}
                            alt={pkg.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>

                        {/* Status Badges */}
                        <div className="absolute top-4 right-4 flex gap-2">
                          <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${
                            pkg.isLive
                              ? 'bg-emerald-500 text-white'
                              : 'bg-gray-500 text-white'
                          }`}>
                            {pkg.isLive ? (
                              <>
                                <div className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></div>
                                Live
                              </>
                            ) : (
                              <>
                                <ClockIcon className="h-3 w-3 mr-1" />
                                Draft
                              </>
                            )}
                          </span>
                          <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${
                            pkg.isActive
                              ? 'bg-blue-500 text-white'
                              : 'bg-red-500 text-white'
                          }`}>
                            {pkg.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>

                        {/* Package Title Overlay */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent p-6">
                          <h3 className="text-xl font-bold text-white mb-2">{pkg.name}</h3>
                          <p className="text-white/90 text-sm line-clamp-2">{pkg.description}</p>
                        </div>
                      </div>

                      {/* Package Details */}
                      <div className="p-6">
                        {/* Key Info */}
                        <div className="grid grid-cols-2 gap-4 mb-6">
                          <div className="flex items-center space-x-2 text-sm">
                            <CalendarIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-600">{pkg.validityPeriod || 12} months</span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm">
                            <UserGroupIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-600">Max {pkg.maxGuests} guests</span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm">
                            <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-600">{pkg.resorts?.length || 0} resorts</span>
                          </div>
                          {pkg.packageCode && (
                            <div className="flex items-center space-x-2 text-sm">
                              <span className="px-2 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded-full">
                                {pkg.packageCode}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Pricing - Enhanced */}
                        <div className="mb-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-gray-500 mb-1 font-medium">Package Price</p>
                              <div className="flex items-baseline gap-2">
                                <div className="text-3xl font-bold text-emerald-600">${pkg.priceUSD}</div>
                                <div className="text-sm text-gray-500">USD</div>
                              </div>
                              <div className="text-sm text-blue-600 font-medium mt-1">
                                ${pkg.priceAUD} AUD
                              </div>
                            </div>
                            {pkg.purchaseCount !== undefined && (
                              <div className="text-right bg-white rounded-lg px-4 py-3 border border-gray-200">
                                <div className="text-xs text-gray-500 font-medium mb-1">Total Sales</div>
                                <div className="text-2xl font-bold text-gray-900">{pkg.purchaseCount}</div>
                                <div className="text-xs text-gray-500 mt-1">purchases</div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Features Grid */}
                        <div className="grid grid-cols-2 gap-3 mb-6">
                          {pkg.inclusions && pkg.inclusions.length > 0 && (
                            <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                                  <span className="text-white text-xs">✓</span>
                                </div>
                                <span className="font-semibold text-emerald-800 text-sm">
                                  Included ({pkg.inclusions.length})
                                </span>
                              </div>
                              <div className="space-y-1">
                                {pkg.inclusions.slice(0, 2).map((item, idx) => (
                                  <div key={idx} className="text-emerald-700 text-xs flex items-start gap-1">
                                    <span className="text-emerald-500 mt-0.5">•</span>
                                    <span className="line-clamp-1">{item}</span>
                                  </div>
                                ))}
                                {pkg.inclusions.length > 2 && (
                                  <div className="text-emerald-600 font-medium text-xs">+{pkg.inclusions.length - 2} more</div>
                                )}
                              </div>
                            </div>
                          )}

                          {pkg.premiumFeatures && pkg.premiumFeatures.length > 0 && (
                            <div className="bg-purple-50 rounded-xl p-3 border border-purple-100">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center">
                                  <span className="text-white text-xs">⭐</span>
                                </div>
                                <span className="font-semibold text-purple-800 text-sm">
                                  Premium ({pkg.premiumFeatures.length})
                                </span>
                              </div>
                              <div className="space-y-1">
                                {pkg.premiumFeatures.slice(0, 2).map((item, idx) => (
                                  <div key={idx} className="text-purple-700 text-xs flex items-start gap-1">
                                    <span className="text-purple-500 mt-0.5">•</span>
                                    <span className="line-clamp-1">{item}</span>
                                  </div>
                                ))}
                                {pkg.premiumFeatures.length > 2 && (
                                  <div className="text-purple-600 font-medium text-xs">+{pkg.premiumFeatures.length - 2} more</div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Action Buttons - Modern & Organized */}
                        <div className="pt-4 border-t border-gray-100">
                          {/* Primary Actions Row */}
                          <div className="flex items-center gap-3 mb-3">
                            {/* Edit - Primary Action */}
                            <button
                              onClick={() => navigate(`/admin/packages/edit/${pkg._id}`)}
                              className="flex-1 inline-flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-sm font-semibold rounded-xl transition-all duration-200 shadow-md hover:shadow-lg"
                            >
                              <PencilSquareIcon className="h-4 w-4 mr-2" />
                              Edit Package
                            </button>

                            {/* View - Primary Action */}
                            <Link
                              to={`/packages/${pkg._id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1 inline-flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white text-sm font-semibold rounded-xl transition-all duration-200 shadow-md hover:shadow-lg"
                            >
                              <EyeIcon className="h-4 w-4 mr-2" />
                              View Live
                            </Link>
                          </div>

                          {/* Secondary Actions Row */}
                          <div className="grid grid-cols-3 gap-2">
                            {/* Toggle Live/Draft Status */}
                            <button
                              onClick={() => handleToggleLive(pkg._id)}
                              className={`inline-flex items-center justify-center px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${
                                pkg.isLive
                                  ? 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300'
                                  : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-300'
                              }`}
                              title={pkg.isLive ? 'Set as Draft' : 'Publish Live'}
                            >
                              {pkg.isLive ? (
                                <>
                                  <EyeSlashIcon className="h-3.5 w-3.5 mr-1.5" />
                                  Set Draft
                                </>
                              ) : (
                                <>
                                  <CheckCircleIcon className="h-3.5 w-3.5 mr-1.5" />
                                  Activate
                                </>
                              )}
                            </button>

                            {/* Clone Package */}
                            <button
                              onClick={() => handleClone(pkg)}
                              className="inline-flex items-center justify-center px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-300 rounded-lg text-xs font-medium transition-all duration-200"
                              title="Duplicate this package"
                            >
                              <DocumentDuplicateIcon className="h-3.5 w-3.5 mr-1.5" />
                              Clone
                            </button>

                            {/* Toggle Active Status */}
                            <button
                              onClick={() => handleToggleStatus(pkg._id)}
                              className={`inline-flex items-center justify-center px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${
                                pkg.isActive
                                  ? 'bg-red-50 hover:bg-red-100 text-red-700 border border-red-300'
                                  : 'bg-green-50 hover:bg-green-100 text-green-700 border border-green-300'
                              }`}
                              title={pkg.isActive ? 'Deactivate package' : 'Activate package'}
                            >
                              {pkg.isActive ? (
                                <>
                                  <XCircleIcon className="h-3.5 w-3.5 mr-1.5" />
                                  Deactivate
                                </>
                              ) : (
                                <>
                                  <CheckCircleIcon className="h-3.5 w-3.5 mr-1.5" />
                                  Activate
                                </>
                              )}
                            </button>
                          </div>

                          {/* Delete Button - Separate & Prominent */}
                          {(pkg.purchaseCount || 0) === 0 && (
                            <button
                              onClick={() => handleDelete(pkg._id)}
                              className="w-full mt-2 inline-flex items-center justify-center px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 border border-red-300 rounded-lg text-xs font-medium transition-all duration-200"
                              title="Delete package permanently"
                            >
                              <TrashIcon className="h-3.5 w-3.5 mr-1.5" />
                              Delete Package
                            </button>
                          )}
                        </div>

                        {/* Warning for packages with purchases */}
                        {(pkg.purchaseCount || 0) > 0 && (
                          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-center space-x-2">
                              <XCircleIcon className="h-4 w-4 text-red-600" />
                              <p className="text-xs text-red-700">
                                Cannot delete: {pkg.purchaseCount || 0} purchases made
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminPackages;
